import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Truck, AlertTriangle, User, Car } from 'lucide-react';

/**
 * Unified LR Issue Form
 * Driver is selected from master (no manual name field)
 * Gate Pass is auto-generated by the system
 */
const LRIssueForm = ({ 
  lrIds = [],
  lrNumbers = [],
  onSuccess,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [formData, setFormData] = useState({
    issue_date: new Date().toISOString().split('T')[0],
    driver_id: '',
    driver_mobile: '',
    vehicle_number: '',
    issued_by: '',
    remarks: ''
  });

  const isBulkIssue = lrIds.length > 1;
  const selectedDriver = drivers.find(d => d.driver_id === formData.driver_id);

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      const response = await api.get('/drivers');
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to fetch drivers', error);
    }
  };

  const handleDriverChange = (driverId) => {
    const driver = drivers.find(d => d.driver_id === driverId);
    if (driver) {
      setFormData(prev => ({
        ...prev,
        driver_id: driverId,
        driver_mobile: driver.mobile_number || '',
        vehicle_number: driver.vehicle_number || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.driver_id || !formData.vehicle_number || !formData.issued_by) {
      toast.error('Please fill all required fields');
      return;
    }

    if (lrIds.length === 0) {
      toast.error('No LRs selected for issuing');
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        issue_date: formData.issue_date,
        driver_id: formData.driver_id,
        driver_mobile: formData.driver_mobile,
        vehicle_number: formData.vehicle_number,
        issued_by: formData.issued_by,
        remarks: formData.remarks || null,
        lr_ids: lrIds
      };

      await api.post('/lr-issues', payload);
      
      toast.success(
        isBulkIssue 
          ? `${lrIds.length} LRs issued for delivery successfully!` 
          : 'LR issued for delivery successfully!'
      );
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue LR(s)');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Selected LRs info */}
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <Truck className="h-4 w-4 text-slate-600" />
          <p className="text-sm font-medium text-slate-900">
            {isBulkIssue ? `Issuing ${lrIds.length} LRs` : 'Issue LR for Delivery'}
          </p>
        </div>
        {lrNumbers.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {lrNumbers.slice(0, 5).map((num) => (
              <span key={num} className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded font-mono">{num}</span>
            ))}
            {lrNumbers.length > 5 && <span className="text-xs text-slate-500">+{lrNumbers.length - 5} more</span>}
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="issue_date">Issue Date *</Label>
        <Input
          id="issue_date"
          data-testid="issue-date-input"
          type="date"
          value={formData.issue_date}
          onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
          required
          className="h-11"
        />
      </div>

      {/* Driver Selection from Master - no manual option */}
      <div>
        <Label htmlFor="driver_select" className="flex items-center gap-1">
          <User className="h-3 w-3" /> Driver *
        </Label>
        <Select value={formData.driver_id} onValueChange={handleDriverChange}>
          <SelectTrigger data-testid="driver-select" className="h-11">
            <SelectValue placeholder="Select driver from master" />
          </SelectTrigger>
          <SelectContent>
            {drivers.map((driver) => (
              <SelectItem key={driver.driver_id} value={driver.driver_id}>
                {driver.driver_name} - {driver.mobile_number}
                {driver.vehicle_number ? ` (${driver.vehicle_number})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Auto-filled driver info */}
      {selectedDriver && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-1">
          <p className="text-sm text-blue-900 font-medium">{selectedDriver.driver_name}</p>
          <p className="text-xs text-blue-700">Mobile: {selectedDriver.mobile_number}</p>
          {selectedDriver.vehicle_number && (
            <p className="text-xs text-blue-700 flex items-center gap-1"><Car className="h-3 w-3" />Vehicle: {selectedDriver.vehicle_number}</p>
          )}
        </div>
      )}

      <div>
        <Label htmlFor="vehicle_number">Vehicle Number *</Label>
        <Input
          id="vehicle_number"
          data-testid="vehicle-number-input"
          value={formData.vehicle_number}
          onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value.toUpperCase() })}
          placeholder="MH12AB1234"
          required
          className="h-11"
        />
      </div>

      {/* Gate Pass auto-generated notice */}
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <p className="text-xs text-slate-500">Gate Pass Number will be auto-generated by the system</p>
      </div>

      <div>
        <Label htmlFor="issued_by">Issued By *</Label>
        <Input
          id="issued_by"
          data-testid="issued-by-input"
          value={formData.issued_by}
          onChange={(e) => setFormData({ ...formData, issued_by: e.target.value })}
          placeholder="Staff name"
          required
          className="h-11"
        />
      </div>

      <div>
        <Label htmlFor="remarks">Remarks (Optional)</Label>
        <Textarea
          id="remarks"
          data-testid="issue-remarks-input"
          value={formData.remarks}
          onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
          placeholder="Any additional notes..."
          rows={3}
        />
      </div>

      {isBulkIssue && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Bulk Issue Warning</p>
            <p className="text-xs text-amber-700 mt-1">
              All {lrIds.length} selected LRs will be marked as "Issued for Delivery" with the same delivery details.
            </p>
          </div>
        </div>
      )}

      <div className="pt-4 flex gap-2">
        <Button
          data-testid="submit-issue-btn"
          type="submit"
          disabled={loading}
          className="flex-1 bg-amber-600 hover:bg-amber-700 h-11"
        >
          {loading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{isBulkIssue ? 'Issuing LRs...' : 'Issuing...'}</>
          ) : (
            <><Truck className="mr-2 h-4 w-4" />{isBulkIssue ? `Issue ${lrIds.length} LRs for Delivery` : 'Issue for Delivery'}</>
          )}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="h-11">Cancel</Button>
        )}
      </div>
    </form>
  );
};

export default LRIssueForm;
